/*eslint no-magic-numbers: ["error", { "ignore": [0] }]*/
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import Input from 'components/Input'
import CheckIcon from 'assets/icons/check-icon'
import Select from 'components/Select'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import Row from 'components/Row'
import Container from 'components/Container'
import Col from 'components/Col'
import { requestUpdateSiteSpecie } from 'api/siteSpecie'
import {
  ECO_BIO_REGION_RESTRICTED_OPTION_B3A_ID,
  ECO_BIO_REGION_RESTRICTED_OPTION_B3B_ID,
  ecoBioRegionRestricted
} from 'constants/ecoBioRegionRestricted'
import capitalize from 'utils/capitalize'
import { delay } from 'utils/delay'
import Textarea from 'components/Textarea'
import { redListCategory } from 'constants/redListCategory'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import ClearIcon from 'assets/icons/clear-icon16px'

import {
  SpeciesDetailsContainer,
  SpeciesDetailsItemWrapper,
  SpeciesDetailsScientificIconWrapper,
  NewSiteHeaderNotification,
  HeaderNotificationCalendarText
} from '../style'
import {
  AssessmentPageSubTittle,
  AssessmentPageSubTittleIconWrapper,
  AssessmentPageSubTittleWrapper,
  AssessmentPageTittle,
  AssessmentPageTittleWrapper
} from '../../../../style'


const redListCategoryOptions = redListCategory.map((item) => {
  return {
    label: item.name,
    value: item.name
  }
})

const rangeRestrictedOptions = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 2 }
]

const sensitiveSpeciesOptions = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 2 }
]

const possiblyExtinctOptions = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 2 }
]

const ecoBioRegionRestrictedOptions = ecoBioRegionRestricted.map((element) => {
  return {
    label: capitalize(element.name),
    value: element.id
  }
})

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const updateSpecieNameTimeOut = delay(inputSaveDelayTime)
const updateTaxonomicGroupTimeOut = delay(inputSaveDelayTime)
const updateRedListCategoryTimeOut = delay(inputSaveDelayTime)
const updateEquivalentTimeOut = delay(inputSaveDelayTime)

const SpeciesDetailsForm = ({ item, site, isEditable }) => {
  const isPrepopulated = item.isRedListCategoryCodePrepopulated
  const params = useParams()

  const redListCategoryInitialValue =
    redListCategoryOptions.find((element) => {
      return element.label === item.RedListCategoryCode
    }) || null

  const redListCriteriaInitialValue = item.IUCNRedListCriteria
    ? {
      label: item.IUCNRedListCriteria,
      value: 1
    }
    : null

  const rangeRestrictedInitialValue = item.rangeRestricted
    ? {
      label: 'Yes',
      value: 1
    }
    : { label: 'No', value: 2 }

  // Database source: sensitiveSpecies (SiteSpecies and SiteSpeciesHistories)
  const sensitiveSpeciesInitialValue = item.sensitiveSpecies === true
    ? {
      label: 'Yes',
      value: 1
    }
    : { label: 'No', value: 2 }

  const possiblyExtinctInitialValue = item.possiblyExtinct
    ? {
      label: 'Yes',
      value: 1
    }
    : { label: 'No', value: 2 }

  const ecoBioRegionInitialValue = ecoBioRegionRestrictedOptions.find((element) => {
    return element.value === item.EcoBioRegionRestrictedId
  }) || { label: 'No', value: 3 }

  const [commonName, setCommonName] = useState(item.specieName || '')
  const [taxonomicGroup, setTaxonomicGroup] = useState(item.taxonomicGroup || '')
  const [redListCategorySelect, setRedListCategorySelect] = useState(redListCategoryInitialValue)
  const [equivalentSystem, setEquivalentSystem] = useState(item.equivalentSystemIfNotRedList || '')
  const [redListCriteria, setRedListCriteria] = useState(redListCriteriaInitialValue)
  const [sensitiveSpecies, setSensitiveSpecies] = useState(sensitiveSpeciesInitialValue)
  const [rangeRestricted, setRangeRestricted] = useState(rangeRestrictedInitialValue)
  const [possiblyExtinct, setPossiblyExtinct] = useState(possiblyExtinctInitialValue)
  const [isNotificationPresent, setIsNotificationPresent] = useState(true)

  const qualified = item.lastReassessmentDate ? item.triggerReasonId.some((element) => {
    // eslint-disable-next-line no-magic-numbers
    return element === 1
  }) : true

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualified

  // Update state when item prop changes (e.g., after API fetch/update)
  // Database source: sensitiveSpecies (SiteSpecies and SiteSpeciesHistories)
  useEffect(() => {
    const newSensitiveSpeciesValue = item.sensitiveSpecies === true
      ? {
        label: 'Yes',
        value: 1
      }
      : { label: 'No', value: 2 }
    setSensitiveSpecies(newSensitiveSpeciesValue)
  }, [item.sensitiveSpecies])

  useEffect(() => {
    const newRangeRestrictedValue = item.rangeRestricted
      ? {
        label: 'Yes',
        value: 1
      }
      : { label: 'No', value: 2 }
    setRangeRestricted(newRangeRestrictedValue)
  }, [item.rangeRestricted])

  useEffect(() => {
    const newRedListCriteriaValue = item.IUCNRedListCriteria
      ? {
        label: item.IUCNRedListCriteria,
        value: 1
      }
      : null
    setRedListCriteria(newRedListCriteriaValue)
  }, [item.IUCNRedListCriteria])

  useEffect(() => {
    const newPossiblyExtinctValue = item.possiblyExtinct
      ? {
        label: 'Yes',
        value: 1
      }
      : { label: 'No', value: 2 }
    setPossiblyExtinct(newPossiblyExtinctValue)
  }, [item.possiblyExtinct])

  useEffect(() => {
    const newRedListCategoryValue =
      redListCategoryOptions.find((element) => {
        return element.label === item.RedListCategoryCode
      }) || null
    setRedListCategorySelect(newRedListCategoryValue)
  }, [item.RedListCategoryCode])

  useEffect(() => {
    setCommonName(item.specieName || '')
  }, [item.specieName])

  useEffect(() => {
    setTaxonomicGroup(item.taxonomicGroup || '')
  }, [item.taxonomicGroup])

  useEffect(() => {
    setEquivalentSystem(item.equivalentSystemIfNotRedList || '')
  }, [item.equivalentSystemIfNotRedList])

  function handleChangeRangeRestricted(e) {
    setRangeRestricted(e)
  }

  function handleChangePossiblyExtinct(e) {
    setPossiblyExtinct(e)
  }

  function handleChangeRedListCriteria(e) {
    setRedListCriteria(e)
  }

  async function handleChangeCommonName(e) {
    setCommonName(e.target.value);

    updateSpecieNameTimeOut(async () => {
      try {
        await requestUpdateSiteSpecie(
          item.id,
          params.siteHistoryId,
          item.historyId,
          { specieName: e.target.value, reassessDateStatus: true }
        );
      } catch (error) {
        console.error("Error updating common name:", error);
      }
    });
  }

  async function handleChangeTaxonomicGroup(e) {
    setTaxonomicGroup(e.target.value);

    updateTaxonomicGroupTimeOut(async () => {
      try {
        await requestUpdateSiteSpecie(
          item.id,
          params.siteHistoryId,
          item.historyId,
          { taxonomicGroup: e.target.value, reassessDateStatus: true }
        );
      } catch (error) {
        console.error("Error updating taxonomic group:", error);
      }
    });
  }

  async function handleChangeRedListCategory(e) {
    setRedListCategorySelect(e);

    updateRedListCategoryTimeOut(async () => {
      try {
        await requestUpdateSiteSpecie(
          item.id,
          params.siteHistoryId,
          item.historyId,
          { RedListCategoryCode: e.value, reassessDateStatus: true }
        );
      } catch (error) {
        console.error("Error updating Red List category:", error);
      }
    });
  }

  async function handleChangeEquivalentSystem(e) {
    setEquivalentSystem(e.target.value);

    updateEquivalentTimeOut(async () => {
      try {
        await requestUpdateSiteSpecie(
          item.id,
          params.siteHistoryId,
          item.historyId,
          { equivalentSystemIfNotRedList: e.target.value, reassessDateStatus: true }
        );
      } catch (error) {
        console.error("Error updating equivalent system:", error);
      }
    });
  }

  const hideNotification = () => {
    setIsNotificationPresent(false)
  }

  return (
    <>
      {isNotificationPresent && (
        <NewSiteHeaderNotification>
          <div />
          <span>
            <HeaderNotificationCalendarText>
              <FormattedMessage id='pleaseContactTheKBASecretariatForAnyDataConcerns' values={{ specieName: <b>{item.scientificName}</b> }} />
            </HeaderNotificationCalendarText>
          </span>

          <div onClick={hideNotification}>
            <ClearIcon />
          </div>
        </NewSiteHeaderNotification>
      )}
      <Container>
        <Row>
          <Col md={8} mdOffset={2}>
            <AssessmentPageTittleWrapper>
              <AssessmentPageTittle data-test='legend'>
                <FormattedMessage id='speciesDetailsForm' />
              </AssessmentPageTittle>
              <AssessmentPageSubTittleWrapper marginBottom='96px'>
                <AssessmentPageSubTittle>
                  <FormattedMessage id='speciesDetailsSubTittle' />
                </AssessmentPageSubTittle>
                <Tooltip
                  style={{ zIndex: 1000 }}
                  zIndex='1070'
                  placement='bottom'
                  trigger={['hover']}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: 'speciesDetailSubTittle', infoTooltip: 'speciesDetailSubTittleHint' }}
                  link={'info@keybiodiversityareas.org'}
                >
                  <AssessmentPageSubTittleIconWrapper>
                    <InfoIcon />
                  </AssessmentPageSubTittleIconWrapper>
                </Tooltip>
              </AssessmentPageSubTittleWrapper>
            </AssessmentPageTittleWrapper>
          </Col>
        </Row>
      </Container>
      <SpeciesDetailsContainer>
        <SpeciesDetailsItemWrapper>
          <Input placeholder={item.scientificName} label='scientificName' disabled={true} />
          <SpeciesDetailsScientificIconWrapper>
            <CheckIcon />
          </SpeciesDetailsScientificIconWrapper>
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Input
            value={commonName}
            onChange={handleChangeCommonName}
            placeholder='enterSpeciesCommonName'
            label='commonName'
            field='specieName'
            isOptional={true}
            disabled={item.isSpecieNamePrepopulated || disabled}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Input
            value={taxonomicGroup}
            onChange={handleChangeTaxonomicGroup}
            placeholder='enterSpeciesTaxonomicGroup'
            label='taxonomicGroup'
            field='taxonomicGroup'
            isOptional={true}
            disabled={item.isTaxonomicGroupPrepopulated || disabled}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Select
            value={redListCategorySelect}
            onChange={handleChangeRedListCategory}
            placeholder='selectSpeciesRedListCategory'
            label='category'
            field='RedListCategoryCode'
            options={redListCategoryOptions}
            height='48px'
            marginBottom='24px'
            infoTooltip='speciesRedListHint'
            zIndex='1070'
            disabled={item.isRedListCategoryCodePrepopulated || disabled}
            isOptional={true}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Select
            value={possiblyExtinct}
            onChange={handleChangePossiblyExtinct}
            label='possiblyExtinct'
            options={possiblyExtinctOptions}
            height='48px'
            marginBottom='24px'
            isOptional={true}
            disabled={true}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Input
            value={isPrepopulated ? item.equivalentSystemIfNotRedList : equivalentSystem}
            onChange={handleChangeEquivalentSystem}
            placeholder='selectEquivalentSystem'
            label='assessmentSystem'
            field='equivalentSystemIfNotRedList'
            infoTooltip='equivalentSystemHint'
            zIndex='1070'
            disabled={site.SiteStatusId !== SITE_STATUS_DRAFT_ID || disabled || isPrepopulated}
            errorMarginTop='8px'
            isOptional={true}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Select
            value={redListCriteria}
            onChange={handleChangeRedListCriteria}
            placeholder='selectSpeciesRedListCriteria'
            label='criteria'
            options={redListCategoryOptions}
            height='48px'
            marginBottom='24px'
            isOptional={true}
            disabled={true}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Select
            value={sensitiveSpecies}
            onChange={() => {}}
            label='globallySensitiveSpecies'
            options={sensitiveSpeciesOptions}
            height='48px'
            marginBottom='24px'
            infoTooltip='globallySensitiveSpeciesHint'
            zIndex='1070'
            isOptional={true}
            disabled={true}
            hyper='https://nc.iucnredlist.org/redlist/content/attachment_files/Mapping_Standards_Version_1.20_Jan2024.pdf'
            textForLink='click here'
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Select
            value={rangeRestricted}
            onChange={handleChangeRangeRestricted}
            label='rangeRestricted'
            options={rangeRestrictedOptions}
            height='48px'
            marginBottom='24px'
            infoTooltip='rangeRestrictedHint'
            zIndex='1070'
            isOptional={true}
            disabled={true}
            link={'info@keybiodiversityareas.org'}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsItemWrapper>
          <Select
            value={ecoBioRegionInitialValue}
            label='ecoBioRegionRestricted'
            field='EcoBioRegionRestrictedId'
            height='48px'
            marginBottom='24px'
            infoTooltip='ecoBioRegionRestrictedHint'
            zIndex='1070'
            disabled={true}
            link={'info@keybiodiversityareas.org'}
          />
        </SpeciesDetailsItemWrapper>
        {ecoBioRegionInitialValue.value === ECO_BIO_REGION_RESTRICTED_OPTION_B3A_ID && (
          <>
            <SpeciesDetailsItemWrapper style={{width: '100%'}}>
              <Textarea
                height='96px'
                label='ecoRegionNameMap'
                field='ecoRegionNameMap'
                value={item.ecoregions}
                placeholder='ecoRegionNameMapPlaceholder'
                disabled={true}
              />
            </SpeciesDetailsItemWrapper>
          </>
        )}
        {ecoBioRegionInitialValue.value === ECO_BIO_REGION_RESTRICTED_OPTION_B3B_ID && (
          <>
            <SpeciesDetailsItemWrapper style={{width: '100%'}}>
              <Textarea
                height='96px'
                label='bioRegionNameMap'
                field='bioRegionNameMap'
                value={item.bioregions}
                placeholder='bioRegionNameMapPlaceholder'
                disabled={true}
              />
            </SpeciesDetailsItemWrapper>
          </>
        )}
      </SpeciesDetailsContainer>
    </>
  )
}

export default SpeciesDetailsForm
